import './scss/UIElements.scss';
import {useIntl} from "react-intl";
import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {ReactComponent as HelpIcon} from "./svg/help.svg"
import {ReactComponent as CloseIcon} from "./svg/close.svg";
import {ReactComponent as CheckIcon} from "./svg/check.svg";
import {ReactComponent as Chevron} from "./svg/chevron.svg";
import {ReactComponent as SnackError} from "./svg/snack-error.svg";
import {ReactComponent as SnackWarning} from "./svg/snack-warning.svg";
import {ReactComponent as SnackInfo} from "./svg/snack-info.svg";
import {ReactComponent as SnackSuccess} from "./svg/snack-success.svg";
import {useWindowSize} from "./formats";
import {useSnackbar} from "./Content";
import {createPortal} from "react-dom";
import {GetSurveyWithId} from "./API";

export function Header({children}) {
    return <header>
        <h1>
            {children}
        </h1>
    </header>
}

export function Skeleton({height, width, circle, inline, marginBottom, classText} ) {
    let className = "skeleton"
    if (circle) {className = className.concat(' circle')}
    if (classText) {className = className.concat(' '+classText)}
    let display = inline ? "inline-block" : "block"
    return <div
        className={className}
        style={{height: height, width: width, display: display, marginBottom: marginBottom}}
    />
}

export function RequestStatus({ status, followup, survey }) {
    const getSurvey = GetSurveyWithId(survey)
    const intl = useIntl()

    const statusMessages = {
        "ACKED": 'seen',
        "OPENED": 'open',
        "NEW": 'new',
        "ERROR": 'error'
    }

    const getStatusElement = (statusKey) => (
        <div className={`status ${statusKey.toLowerCase()}`}>
            {intl.formatMessage({
                id: statusKey.toLowerCase(),
                defaultMessage: statusKey.charAt(0) + statusKey.slice(1).toLowerCase(),
            })}
        </div>
    )

    const renderTestTag = getSurvey.isSuccess &&
        getSurvey?.data?.[0]?.config?.type === "TEST"

    const testTagElement = (
        <div className={"status test"}
             title={intl.formatMessage({
                 id: 'requestWasCreatedWithTest',
                 defaultMessage: 'This support request was created with a test survey',
             })}>
            {intl.formatMessage({
                id: 'surveytype.TEST',
                defaultMessage: 'Test Survey',
            })}
        </div>
    )

    const followupElement = followup === "FINISHED" ? (
        <div className={"status followup done"}
             title={intl.formatMessage({
                 id: 'followup.done',
                 defaultMessage: 'Follow up survey finished',
             })}>
            {intl.formatMessage({
                id: 'followup.over',
                defaultMessage: 'Follow up over',
            })}
        </div>
    ) : (
        <div className={"status followup in-progress"}
             title={intl.formatMessage({
                 id: 'followup.inProgress',
                 defaultMessage: 'Follow up active',
             })}>
            {intl.formatMessage({
                id: 'followup',
                defaultMessage: 'Follow up',
            })}
        </div>
    )

    return (
        <div className={"status-container"}>
            {status === "NEW" && getStatusElement(statusMessages["OPENED"])}
            {statusMessages[status] && getStatusElement(statusMessages[status])}
            {followup && followupElement}
            {renderTestTag && testTagElement}
        </div>
    )
}

export function Popover({children, showPopover, setShowPopover, popoverTriggerRef}) {
    const popoverRef = useRef(null)

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !popoverTriggerRef.current.contains(event.target) ) {
                setShowPopover(false)
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popoverRef])
    return <>
        {showPopover &&
        <div className={"popover"} ref={popoverRef}>
            {children}
        </div>
        }
    </>
}

export function Tooltip({children}) {
    const [showPopover, setShowPopover] = useState(false)
    const popoverRef = useRef(null)
    const popoverTriggerRef = useRef(null)
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !popoverTriggerRef.current.contains(event.target) ) {
                setShowPopover(false)
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popoverRef])
    return <>
        <button className={"tooltip"} ref={popoverTriggerRef} onClick={()=>setShowPopover(!showPopover)}><HelpIcon/></button>
        {showPopover &&
        <div className={"popover"} ref={popoverRef}>
            {children}
            <button className={"icon-button"} onClick={()=>setShowPopover(false)}>
                <CloseIcon />
            </button>
        </div>
        }
    </>
}

export function HoverTooltip({ className, children, text }) {
    const [showTooltip, setShowTooltip] = useState(false)
    return <div className={`${className} tooltip-container`}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={() => setShowTooltip(true)}
    >
        <div className={"tooltip"}>
            <HelpIcon/>
        </div>
        {showTooltip &&
            <div className={"popover hover"}>
                {children}
                <button className={"icon-button"} onClick={(e)=> {
                    e.stopPropagation()
                    setShowTooltip(false)
                }}>
                    <CloseIcon />
                </button>
            </div>
        }
    </div>
}

export function Switch({id, name, checked, onChange, disabled, title}) {
    if (disabled) {
        return <>
            <label aria-hidden={true} htmlFor={id} className={"switch disabled"} title={title}>
                <span className={"ball"} />
                <span className={"track"} />
            </label>
            <input type={"checkbox"} className={"hidden-element disabled"} disabled id={id} name={name} checked={false}/>
        </>
    }
    return <>
        <label aria-hidden={true} htmlFor={id} className={checked ? "switch checked" : "switch"}>
            <span className={"ball"} />
            <span className={"track"} />
        </label>
        <input type={"checkbox"} className={"hidden-element"} id={id} name={name} checked={checked} onChange={()=>onChange()}/>
        </>
}

export function Popup({onClose, header, children, className, stopPropagation}) {
    const popupRef = useRef(null)
    // Stop body scroll on popup overlay
    useEffect(() => {
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = null
        }
    }, [])
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target) ) {
                onClose()
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popupRef, onClose])
    const [scrollOverflow, setScrollOverflow] = useState(false)
    React.useLayoutEffect(() => {
        if (popupRef.current.clientHeight >= (window.innerHeight - 30)) {
            setScrollOverflow(true)
        }
    }, [popupRef?.current?.clientHeight])

    const overflowClass = scrollOverflow ? " overflow" : ""
    const classNameClass = className ? " " + className : ""
    const popupClass = "popup" + classNameClass + overflowClass

    return <>
        <div className={"overlay"} onClick={stopPropagation? (e)=> {e.stopPropagation();e.preventDefault();} : null}>
            <div className={popupClass} ref={popupRef}>
                <h1>
                    {header}
                    <button className={"icon-button"} onClick={()=>onClose()}>
                        <CloseIcon />
                    </button>
                </h1>
                {children}
            </div>
        </div>
    </>
}

export function SearchSelectDropdown({children, value, placeholder, setSearchSelectValue, removeFilter, disabled, scrolledToBottom}) {
    const intl = useIntl()
    const [selectOpen, setSelectOpen] = useState(false)
    const [searchValue, setSearchValue] = useState("")
    const selectRef = useRef(null)
    const toggleRef = useRef(null)
    const popoverRef = useRef(null)
    const windowSize = useWindowSize()
    const [popoverMaxHeight, setPopoverMaxHeight] = useState(undefined)
    const [originalValue] = useState(value)

    // Calculate the maxHeight of popover element for overflow scroll
    useEffect(()=>{
        if (popoverRef?.current && windowSize?.height) {
            const fromTop = popoverRef?.current.getBoundingClientRect().top
            const maxHeight = Math.round(windowSize?.height - fromTop - 15)
            setPopoverMaxHeight(maxHeight)
        }
    },[selectOpen, popoverRef?.current, windowSize?.height])

    // Close dropdown when clicking outside container
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (selectRef.current && !selectRef.current.contains(event.target) && !toggleRef.current.contains(event.target)) {
                setSelectOpen(false)
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[selectRef, toggleRef])

    useEffect(()=>{
        if (originalValue !== value) {
            setSelectOpen(false)
        }
    },[value])

    const handleScroll = e => {
        if ((e.target.scrollHeight - 200) - e.target.scrollTop <= e.target.clientHeight) {
            scrolledToBottom()
        }
    }

    return !disabled ?
        <div className={"search-select-container"} ref={selectRef}>
        <button className={"search-select-toggle" + (value ? " active" : "") + (selectOpen ? " open" : "")} onClick={()=>setSelectOpen(!selectOpen)} ref={toggleRef}>
            <span className={"overflow-ellipsis"}>{value ? value : placeholder}</span>
            <Chevron />
        </button>
        {removeFilter && value &&
            <button onClick={()=>removeFilter()} className={"icon-button"}>
                <CloseIcon />
            </button>
        }
        {selectOpen &&
            <div className={"search-select-popover"} onScroll={scrolledToBottom && handleScroll} ref={popoverRef} style={{maxHeight:popoverMaxHeight}}>
                <input autoFocus={true} type="search" value={searchValue} onChange={e=> {
                    setSearchSelectValue(e.target.value)
                    setSearchValue(e.target.value)
                }}
                       placeholder={intl.formatMessage(
                           {
                               id: 'typeToSearch',
                               defaultMessage: 'Type to Search',
                           })}/>
                <div className={"options-container"}>
                    {children}
                </div>
            </div>
        }
    </div> :
        <div className={"search-select-container"}>
            <button className={"search-select-toggle disabled"} disabled>
                {value ? value : placeholder}
                <Chevron />
            </button>
        </div>
}

export function SearchSelectOption({value, setSelectValue, disabled, children, active}) {
    return disabled ?
        <button className={"search-select-option disabled"}>✓ {children}</button> :
        <button className={active ? "search-select-option active" : "search-select-option"} onClick={()=>setSelectValue(value)}>{active && "✓ "}{children}</button>
}

export function Snackbar({show, text, state, autoHide}) {
    const {setSnackbarData} = useSnackbar()
    const icon = state === "error" ? <SnackError /> :
        state === "warning" ? <SnackWarning /> :
            state === "success" ? <SnackSuccess /> :
                <SnackInfo />

    const hideSnackbar = <button className={"hide-snackbar"}
                                 onClick={e=> {
                                     e.stopPropagation()
                                     setSnackbarData(state => ({
                                         ...state,
                                         show: false
                                     }))
                                 }}>
        <CloseIcon />
    </button>

    useEffect(()=> {
        let timeOutAutoHide = setTimeout(()=> {
            if (autoHide) {
                setSnackbarData(state => ({
                    ...state,
                    show: false
                }))
            }
        }, 5000)

        return () => {clearTimeout(timeOutAutoHide)}
    },[show, text, state, autoHide])

    return <>
        {show &&
        <div className={"snackbar"} data-state={state}>
            {icon}
            <p>{text}</p>
            {hideSnackbar}
        </div>
        }
    </>
}

const PopupContext = createContext(null)

export const usePopup = () => useContext(PopupContext)

export const PopupProvider = ({ children }) => {
    const [isPopupOpen, setPopupOpen] = useState(false)
    const [popupContent, setPopupContent] = useState(null)
    const [popupClassName, setPopupClassName] = useState('')
    const [popupHeader, setPopupHeader] = useState('')

    // Stop body scroll on popup overlay
    useEffect(() => {
        if (isPopupOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = null
        }

        return () => {
            document.body.style.overflow = null
        }
    }, [isPopupOpen])

    const showPopup = (content, className = '', header = '') => {
        setPopupContent(content)
        setPopupClassName(className)
        setPopupHeader(header)
        setPopupOpen(true)
    }

    const hidePopup = () => {
        setPopupOpen(false)
        setPopupContent(null)
        setPopupClassName('')
        setPopupHeader('')
    }

    return (
        <PopupContext.Provider value={{ isPopupOpen, popupContent, popupClassName, showPopup, hidePopup, popupHeader }}>
            {children}
        </PopupContext.Provider>
    )
}

export const PopupElement = () => {
    const { isPopupOpen, popupContent, popupClassName, hidePopup, popupHeader } = usePopup()
    const refPopup = useRef(null)

    useEffect(() => {
        const checkIfClickedOutside = e => {
            if (isPopupOpen && refPopup.current && !refPopup.current.contains(e.target)) {
                hidePopup()
            }
        }
        document.addEventListener("mousedown", checkIfClickedOutside)
        return () => {
            // Cleanup the event listener
            document.removeEventListener("mousedown", checkIfClickedOutside)
        }

    }, [isPopupOpen, hidePopup])

    if (!isPopupOpen) return null

    return createPortal(
        <PopupContent refPopup={refPopup} hidePopup={hidePopup} popupContent={popupContent} popupClassName={popupClassName} popupHeader={popupHeader}/>
        ,
        document.body
    )
}

function PopupContent({refPopup, hidePopup, popupContent, popupClassName, popupHeader}) {
    const [scrollOverflow, setScrollOverflow] = useState(false)

    React.useLayoutEffect(() => {
        if (refPopup.current.clientHeight >= (window.innerHeight - 30)) {
            setScrollOverflow(true)
        }
    }, [refPopup?.current?.clientHeight])

    const overflowClass = scrollOverflow ? " overflow" : ""
    const classNameClass = popupClassName ? " " + popupClassName : ""
    const popupClass = "popup" + classNameClass + overflowClass

    return <div className={"overlay"}>
        <div ref={refPopup} className={popupClass}>
            <h1>
                {popupHeader}
                <button className={"icon-button"} onClick={hidePopup}>
                    <CloseIcon />
                </button>
            </h1>
            {popupContent}
        </div>
    </div>
}
